import React from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { OverflowProbe } from './shared/dev/OverflowProbe';
import { RouterProvider } from 'react-router-dom';
import { Toaster } from 'sonner';
import { router } from './router';
import { queryClient } from './shared/query-client';
import { ThemeProvider } from './shared/theme/ThemeControls';
import { useAppearance } from './shared/theme/use-appearance';
import './styles/globals.css';

/**
 * Application entry.
 *
 * The query-client defaults encode two product decisions:
 *   • Never retry a 4xx. A 402 plan gate or a 403 will fail identically three
 *     times and only delay the message the user needs to see.
 *   • Refetch on window focus. An admin leaves the complaints board open all
 *     day; stale counts there are worse than an extra request.
 */
// The query client lives in shared/query-client so signing out can empty it.

/**
 * Applies the platform's saved colours and fonts before anything draws.
 *
 * Outside the router on purpose: the sign-in page has to paint in the right
 * colour too, and that page is rendered before any authenticated provider
 * exists.
 */
function Themed({ children }: { children: React.ReactNode }): React.JSX.Element {
  useAppearance();
  return <>{children}</>;
}

const container = document.getElementById('root');
if (!container) throw new Error('Root element is missing from index.html');

createRoot(container).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Themed>
          <RouterProvider router={router} />
        </Themed>
      </ThemeProvider>
      <Toaster
        position="bottom-right"
        closeButton
        richColors
        toastOptions={{
          classNames: {
            toast: 'rounded-sk-lg border border-outline shadow-sk-md font-sans',
            title: 'text-sm font-semibold',
            description: 'text-[13px] text-ink-2',
          },
        }}
      />
      {/* Opt-in: its floating button sits where save bars put theirs. VITE_QUERY_DEVTOOLS=true to show it. */}
      {import.meta.env.DEV && import.meta.env['VITE_QUERY_DEVTOOLS'] === 'true' ? <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" /> : null}
      {import.meta.env.DEV ? <OverflowProbe /> : null}
    </QueryClientProvider>
  </React.StrictMode>,
);
